<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import {
  TRANSACTION_TYPE_OPTIONS,
  REAL_ESTATE_CATEGORY_OPTIONS,
  USAGE_AREA,
  DIRECTION_OPTIONS,
  PRICE_TYPE_OPTIONS,
  PRICE_TYPE_UNIT_MAP,
  LEGAL_STATUS_OPTIONS,
  TRANSACTION_TYPE,
} from '@/modules/realestate/constants/realestate.const'
import Editor from 'primevue/editor'
import { useListingDetailStore } from '@/modules/realestate/stores/listing.detail.store.ts'
import { numHelper } from '@/utils/numHelper.ts'
import ListingDetailContentAttributes from './ListingDetailContentAttributes.vue'

interface Props {
  editable: boolean
  form?: any
  newData?: any
}

const listingDetailStore = useListingDetailStore()

const requestFetchDetail = async () => {
  try {
    await listingDetailStore.fetchAttributes()
  } catch (e) {
    console.error(e)
  }
}

onMounted(() => {
  requestFetchDetail()
})

const props = defineProps<Props>()
const _description = ref('')
const _real_estate_category = ref<any>({})
const _direction = ref({})
const _price = ref<number | null>(null)
const _price_type = ref<any>({})
const _transaction_type = ref<any>({})

const editable = computed(() => props.editable)

const initValues = computed(() => props.form?.initialValues || {})

watch(
  () => initValues.value,
  (newValue: any) => {
    if (newValue) {
      _description.value = newValue.description || ''
      _real_estate_category.value = newValue.real_estate_category || ''
      _direction.value = newValue.direction || {}
      _price.value = newValue.price ?? null
      _price_type.value = newValue.price_type || {}
      _transaction_type.value = newValue.transaction_type || {}
    }
  },
  { immediate: true },
)

const fields = computed(() => props.form?.fields || {})
const newData = computed(() => props.newData || null)

const formControlOldClass = computed(() => '!bg-red-100 !box-border !px-2 !py-1')
const formControlNewClass = computed(() => 'form-control !bg-green-100 !text-green-800 !px-2 !py-1')

/** Dynamic price unit label based on selected price_type */
const priceUnit = computed(() => {
  const code = _price_type.value?.code || fields.value?.price_type?.states?.value?.code
  return PRICE_TYPE_UNIT_MAP[code] || 'VNĐ'
})

/** Show price_deposit only for rent transactions */
const isRent = computed(() => {
  const code = _transaction_type.value?.code || fields.value?.transaction_type?.states?.value?.code
  return code === TRANSACTION_TYPE.RENT
})
</script>

<template>
  <div class="flex flex-col w-full pt-3 gap-y-5">
    <InputText type="text" hidden name="direction" />
    
    <div class="flex flex-col">
      <!-- Thông tin giao dịch & loại BĐS - 2 cột -->
      <div class="grid md:grid-cols-2 gap-4 mb-4">
        <!-- Loại hình giao dịch -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Loại hình:</div>
          <div class="w-full space-y-1">
            <Select
              v-model="_transaction_type"
              name="transaction_type"
              :options="TRANSACTION_TYPE_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
              optionLabel="name"
              placeholder="Chọn loại hình"
              :class="`form-control ${
                newData && fields?.transaction_type?.states?.value?.code !== newData?.transaction_type
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <template v-if="newData && fields?.transaction_type?.states?.value?.code !== newData?.transaction_type">
              <InputText
                :value="TRANSACTION_TYPE_OPTIONS.find((item: any) => item.value === newData?.transaction_type)?.label"
                disabled
                :class="formControlNewClass"
              />
            </template>
          </div>
        </div>

        <!-- Loại bất động sản -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Loại bất động sản:</div>
          <div class="w-full space-y-1">
            <Select
              v-model="_real_estate_category"
              name="real_estate_category"
              :options="REAL_ESTATE_CATEGORY_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
              optionLabel="name"
              placeholder="Chọn loại bất động sản"
              :class="`form-control ${
                newData && _real_estate_category?.code !== newData?.real_estate_category
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ fields?.real_estate_category?.states?.error?.message }}
            </span>
            <template v-if="newData && _real_estate_category?.code !== newData?.real_estate_category">
              <InputText
                :value="REAL_ESTATE_CATEGORY_OPTIONS.find((item: any) => item.value === newData?.real_estate_category)?.label"
                disabled
                :class="formControlNewClass"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Tình trạng pháp lý & Hướng nhà - 2 cột -->
      <div class="grid md:grid-cols-2 gap-4 mb-4">
        <!-- Tình trạng pháp lý -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Pháp lý:</div>
          <div class="w-full space-y-1">
            <Select
              name="legal_status"
              :options="LEGAL_STATUS_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
              optionLabel="name"
              placeholder="Chọn tình trạng pháp lý"
              :class="`form-control ${
                newData && fields?.legal_status?.states?.value?.code !== newData?.legal_status
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <template v-if="newData && fields?.legal_status?.states?.value?.code !== newData?.legal_status">
              <InputText
                :value="LEGAL_STATUS_OPTIONS.find((item: any) => item.value === newData?.legal_status)?.label"
                disabled
                :class="formControlNewClass"
              />
            </template>
          </div>
        </div>

        <!-- Hướng nhà (facade direction) -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Hướng nhà:</div>
          <div class="w-full space-y-1">
            <Select
              v-model="_direction"
              name="direction"
              :options="DIRECTION_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
              optionLabel="name"
              placeholder="Chọn hướng nhà"
              :class="`form-control ${
                newData && fields?.direction?.states?.value?.code !== newData?.direction
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ fields?.direction?.states?.error?.message }}
            </span>
            <template v-if="newData && fields?.direction?.states?.value?.code !== newData?.direction">
              <InputText
                :value="DIRECTION_OPTIONS.find((item: any) => item.value === newData?.direction)?.label"
                disabled
                :class="formControlNewClass"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Diện tích đất & Diện tích sử dụng - 2 cột -->
      <div class="grid md:grid-cols-2 gap-4 mb-4">
        <!-- Diện tích -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Diện tích đất:</div>
          <div class="w-full space-y-1">
            <InputNumber
              name="area"
              placeholder="Diện tích (m²)"
              :class="`form-control ${
                newData && fields?.area?.states?.value !== newData?.area ? formControlOldClass : ''
              }`"
              :disabled="!editable"
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ fields?.area?.states?.error?.message }}
            </span>
            <template v-if="newData && fields?.area?.states?.value !== newData?.area">
              <InputText :value="newData?.area" disabled :class="formControlNewClass" />
            </template>
          </div>
        </div>

        <!-- Diện tích sử dụng (conditional) -->
        <div
          class="flex flex-col gap-1"
          v-if="
            USAGE_AREA.includes(_real_estate_category?.code) ||
            (newData && USAGE_AREA.includes(newData?.real_estate_category))
          "
        >
          <div class="font-medium mb-1">Diện tích sử dụng:</div>
          <div class="w-full space-y-1">
            <InputNumber
              name="usage_area"
              placeholder="Diện tích sử dụng (m²)"
              :class="`form-control ${
                newData && fields?.usage_area?.states?.value !== newData?.usage_area
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <template v-if="newData && fields?.usage_area?.states?.value !== newData?.usage_area">
              <InputText :value="newData?.usage_area" disabled :class="formControlNewClass" />
            </template>
          </div>
        </div>
      </div>

      <!-- Category-specific fields component -->
      <ListingDetailContentAttributes
        :editable="editable"
        :form="props.form"
        :newData="props.newData"
        :real-estate-category="_real_estate_category?.code"
        :transaction-type="_transaction_type?.code"
      />

      <Divider class="my-4" />

      <!-- Thông tin giá -->
      <div class="mb-4">
        <!-- Loại giá & Giá - 2 cột -->
        <div class="grid md:grid-cols-2 gap-4 mb-4">
          <!-- Loại giá -->
          <div class="flex flex-col gap-1">
            <div class="font-medium mb-1">Loại giá:</div>
            <div class="w-full space-y-1">
              <Select
                v-model="_price_type"
                name="price_type"
                :options="PRICE_TYPE_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
                optionLabel="name"
                placeholder="Chọn loại giá"
                :class="`form-control ${
                  newData && fields?.price_type?.states?.value?.code !== newData?.price_type
                    ? formControlOldClass
                    : ''
                }`"
                :disabled="!editable"
              />
              <template v-if="newData && fields?.price_type?.states?.value?.code !== newData?.price_type">
                <InputText
                  :value="PRICE_TYPE_OPTIONS.find((item: any) => item.value === newData?.price_type)?.label"
                  disabled
                  :class="formControlNewClass"
                />
              </template>
            </div>
          </div>

          <!-- Giá -->
          <div class="flex flex-col gap-1">
            <div class="font-medium mb-1">Giá ({{ priceUnit }}):</div>
            <div class="w-full">
              <InputNumber
                name="price"
                v-model="_price"
                placeholder="Nhập giá"
                :class="`form-control ${
                  newData && fields?.price?.states?.value !== newData?.price ? formControlOldClass : ''
                }`"
                :disabled="!editable"
              />
              <span class="font-italic text-[12px] text-gray-600">
                {{ _price ? numHelper(_price).numberToVietnameseText({ unit: 'đồng' }) : '' }}
              </span>
              <span class="font-italic text-[12px] text-red-500">
                {{ fields?.price?.states?.error?.message }}
              </span>
              <template v-if="newData && fields?.price?.states?.value !== newData?.price">
                <InputText :value="newData?.price" disabled :class="formControlNewClass" />
                <span class="font-italic text-[12px] text-gray-600">
                  {{ numHelper(newData?.price).numberToVietnameseText({ unit: 'đồng' }) }}
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- Đặt cọc (chỉ hiển thị khi cho thuê) -->
        <div class="flex flex-col gap-1" v-if="isRent || (newData && newData?.price_deposit)">
          <div class="font-medium mb-1">Đặt cọc (VNĐ):</div>
          <div class="w-full space-y-1">
            <InputNumber
              name="price_deposit"
              placeholder="Số tiền đặt cọc"
              :class="`form-control ${
                newData && fields?.price_deposit?.states?.value !== newData?.price_deposit
                  ? formControlOldClass
                  : ''
              }`"
              :disabled="!editable"
            />
            <template v-if="newData && fields?.price_deposit?.states?.value !== newData?.price_deposit">
              <InputText :value="newData?.price_deposit" disabled :class="formControlNewClass" />
            </template>
          </div>
        </div>
      </div>

      <Divider class="my-4" />

      <!-- Nội dung tin đăng -->
      <div class="mb-4">
        <!-- Tiêu đề -->
        <div class="flex flex-col gap-1 mb-4">
          <div class="font-medium mb-1">Tiêu đề:</div>
          <div class="w-full space-y-1">
            <InputText
              name="title"
              placeholder="Nhập tiêu đề tin đăng"
              :class="`form-control ${
                newData && fields?.title?.states?.value !== newData?.title ? formControlOldClass : ''
              }`"
              :disabled="!editable"
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ fields?.title?.states?.error?.message }}
            </span>
            <template v-if="newData && fields?.title?.states?.value !== newData?.title">
              <InputText :value="newData?.title" disabled :class="formControlNewClass" />
            </template>
          </div>
        </div>

        <!-- Nội dung mô tả -->
        <div class="flex flex-col gap-1">
          <div class="font-medium mb-1">Mô tả chi tiết:</div>
          <div class="w-full space-y-1">
            <template v-if="!editable">
              <div
                v-html="_description"
                :class="`text-sm ${
                  newData && _description !== newData?.description ? formControlOldClass : ''
                }`"
              ></div>
            </template>
            <template v-if="newData && _description !== newData?.description">
              <div v-html="newData?.description" :class="formControlNewClass"></div>
            </template>
            <Editor
              v-if="editable"
              v-model="_description"
              name="description"
              editorStyle="height: 250px"
              :class="
                !!fields?.description?.states?.error?.message
                  ? 'border border-[var(--p-inputtext-invalid-border-color)] rounded'
                  : ''
              "
            >
              <template v-slot:toolbar>
                <span class="ql-formats">
                  <button v-tooltip.bottom="'Bold'" class="ql-bold"></button>
                  <button v-tooltip.bottom="'Italic'" class="ql-italic"></button>
                  <button v-tooltip.bottom="'Underline'" class="ql-underline"></button>
                </span>
              </template>
            </Editor>
            <span class="font-italic text-[12px] text-red-500">
              {{ fields?.description?.states?.error?.message }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
